import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { useHistory } from 'react-router-dom'

import {
    Modal,
    Paper,
    Slide,
    Typography,
    TextField,
    Button,
    Box,
    LinearProgress,
    IconButton,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
} from '@material-ui/core'
import {
    Close as CloseIcon,
    TableChart as SpreadsheetIcon,
    AccountBalance as AccountBalanceIcon,
} from '@material-ui/icons'
import { makeStyles, Theme } from '@material-ui/core/styles'

import { axiosInstance } from '../../../helpers'
import { Routes } from '../../../navigation/config/routes'
import { AuditType } from '../types'

interface CreateLeaseAuditModalProps {
    open: boolean
    onClose: () => void
    onSuccess: () => void
}

interface CreateAuditResponse {
    id: number
    name: string
    status: string
    rent_roll_file_name?: string
    rent_roll_presigned_url?: string
    created_date: string
}

const useStyles = makeStyles((theme: Theme) => ({
    modalContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    paper: {
        padding: theme.spacing(4),
        width: 500,
        maxWidth: '90vw',
        maxHeight: '90vh',
        overflow: 'auto',
        outline: 'none',
        borderRadius: theme.spacing(2),
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing(3),
    },
    title: {
        fontWeight: 'bold',
    },
    sectionTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(1),
        fontWeight: 600,
    },
    sectionIcon: {
        fontSize: 20,
        color: theme.palette.primary.main,
    },
    requiredLabel: {
        color: theme.palette.error.main,
        fontSize: '0.75rem',
        marginLeft: theme.spacing(0.5),
    },
    dropzone: {
        border: `2px dashed ${theme.palette.grey[300]}`,
        borderRadius: theme.spacing(2),
        padding: theme.spacing(3),
        textAlign: 'center',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        backgroundColor: theme.palette.grey[50],
        '&:hover': {
            borderColor: theme.palette.primary.main,
            backgroundColor: theme.palette.primary.light + '10',
        },
    },
    dropzoneActive: {
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.primary.light + '20',
    },
    dropzoneDisabled: {
        cursor: 'not-allowed',
        opacity: 0.6,
    },
    uploadIcon: {
        fontSize: 40,
        color: theme.palette.grey[400],
        marginBottom: theme.spacing(1),
    },
    fileInfo: {
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(1.5),
        backgroundColor: theme.palette.grey[100],
        borderRadius: theme.spacing(1),
        marginTop: theme.spacing(1.5),
    },
    fileIconSpreadsheet: {
        color: '#10b981',
        marginRight: theme.spacing(1.5),
    },
    fileName: {
        flex: 1,
        fontWeight: 500,
        fontSize: '0.875rem',
    },
    fileSize: {
        color: theme.palette.grey[600],
        fontSize: '0.75rem',
    },
    removeButton: {
        marginLeft: theme.spacing(1),
    },
    uploadSection: {
        marginBottom: theme.spacing(3),
    },
    progressContainer: {
        marginTop: theme.spacing(3),
    },
    progressItem: {
        marginBottom: theme.spacing(2),
    },
    progressLabel: {
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(0.5),
        fontSize: '0.875rem',
    },
    progressBar: {
        height: 8,
        borderRadius: 4,
    },
    actions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: theme.spacing(2),
        marginTop: theme.spacing(3),
    },
    cancelButton: {
        textTransform: 'none',
    },
    submitButton: {
        textTransform: 'none',
        minWidth: 120,
    },
}))

export const CreateLeaseAuditModal: React.FC<CreateLeaseAuditModalProps> = ({
    open,
    onClose,
    onSuccess,
}) => {
    const classes = useStyles()
    const history = useHistory()
    const requiredTokens = 3000
    const formatTokens = (value: number): string =>
        value.toLocaleString('en-US')

    const [name, setName] = useState('')
    const [auditType, setAuditType] = useState<AuditType | null>(null)
    const [isCreating, setIsCreating] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [tokenBalance, setTokenBalance] = useState<number | null>(null)
    const [isLoadingTokenBalance, setIsLoadingTokenBalance] = useState(false)
    const [tokenBalanceError, setTokenBalanceError] = useState<string | null>(
        null,
    )

    const hasSufficientTokens =
        tokenBalance !== null && tokenBalance > requiredTokens

    const resetForm = () => {
        setName('')
        setAuditType(null)
        setIsCreating(false)
        setError(null)
        setTokenBalance(null)
        setIsLoadingTokenBalance(false)
        setTokenBalanceError(null)
    }

    const handleClose = () => {
        if (!isCreating) {
            resetForm()
            onClose()
        }
    }

    const handleSubmit = async () => {
        // Validation
        if (!name.trim()) {
            setError('Please enter an audit name')
            return
        }
        if (!auditType) {
            setError('Please select an audit type')
            return
        }
        if (!hasSufficientTokens) {
            setError(
                `Insufficient tokens. You need more than ${requiredTokens} tokens to create a lease audit.`,
            )
            return
        }

        setIsCreating(true)
        setError(null)

        try {
            // Send JSON data instead of FormData
            const createResponse = await axiosInstance.post<CreateAuditResponse>(
                '/lease-audit/',
                {
                    name: name.trim(),
                    audit_type: auditType,
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                },
            )

            // Check for successful creation (201 status)
            if (createResponse.status === 201) {
                const { id } = createResponse.data

                toast.success(
                    'Lease audit created successfully! Navigating to audit details...',
                )
                resetForm()
                onSuccess()
                onClose()

                // Navigate to lease audit detail page
                history.push(
                    Routes.manage.lease.auditDetail.replace(
                        ':id',
                        id.toString(),
                    ),
                )
            }
        } catch (err: any) {
            console.error('Error creating lease audit:', err)
            const errorMessage =
                err?.response?.data?.error ||
                err?.response?.data?.message ||
                'Failed to create lease audit. Please try again.'
            setError(errorMessage)
            toast.error(errorMessage)
            setIsCreating(false)
        }
    }

    useEffect(() => {
        if (!open) {
            return
        }

        let isActive = true
        const fetchTokenBalance = async () => {
            setIsLoadingTokenBalance(true)
            setTokenBalanceError(null)
            try {
                const response = await axiosInstance.get<number>(
                    '/lease-audit/0/token-balance/',
                )
                if (isActive) {
                    setTokenBalance(response.data)
                }
            } catch (err: any) {
                if (isActive) {
                    setTokenBalanceError(
                        err?.response?.data?.message ||
                            'Unable to load token balance.',
                    )
                }
            } finally {
                if (isActive) {
                    setIsLoadingTokenBalance(false)
                }
            }
        }

        fetchTokenBalance()
        return () => {
            isActive = false
        }
    }, [open])

    return (
        <Modal
            open={open}
            onClose={handleClose}
            className={classes.modalContainer}
        >
            <Slide direction="up" in={open}>
                <Paper className={classes.paper}>
                    {/* Header */}
                    <Box className={classes.header}>
                        <Typography variant="h5" className={classes.title}>
                            Create New Lease Audit
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={handleClose}
                            disabled={isCreating}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {/* Name Field */}
                    <TextField
                        label="Audit Name"
                        placeholder="e.g., Lofts on St. James - March"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        fullWidth
                        variant="outlined"
                        disabled={isCreating}
                        style={{ marginBottom: 24 }}
                    />

                    {/* Audit Type Radio Buttons */}
                    <Box className={classes.uploadSection}>
                        <Typography
                            variant="subtitle2"
                            className={classes.sectionTitle}
                        >
                            <SpreadsheetIcon
                                className={classes.sectionIcon}
                                style={{ color: '#10b981' }}
                            />
                            Data Source
                            <span className={classes.requiredLabel}>
                                *Required
                            </span>
                        </Typography>

                        <FormControl component="fieldset" disabled={isCreating}>
                            <RadioGroup
                                value={auditType || ''}
                                onChange={(e) =>
                                    setAuditType(
                                        e.target.value as
                                            | 'UPLOADED'
                                            | 'INTEGRATED',
                                    )
                                }
                            >
                                <FormControlLabel
                                    value="UPLOADED"
                                    control={<Radio color="primary" />}
                                    label={
                                        <Box>
                                            <Typography
                                                variant="body1"
                                                style={{ fontWeight: 500 }}
                                            >
                                                Uploaded
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color="textSecondary"
                                            >
                                                Upload audit data after creation
                                            </Typography>
                                        </Box>
                                    }
                                    style={{ marginBottom: 16 }}
                                />
                                <FormControlLabel
                                    value="INTEGRATED"
                                    control={<Radio color="primary" />}
                                    label={
                                        <Box>
                                            <Typography
                                                variant="body1"
                                                style={{ fontWeight: 500 }}
                                            >
                                                Integrated
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color="textSecondary"
                                            >
                                                Use interal lease data
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </RadioGroup>
                        </FormControl>
                    </Box>

                    {/* Creating Progress */}
                    {isCreating && (
                        <Box className={classes.progressContainer}>
                            <Box className={classes.progressItem}>
                                <LinearProgress
                                    className={classes.progressBar}
                                />
                            </Box>

                            <Typography
                                variant="body2"
                                color="textSecondary"
                                align="center"
                                style={{ marginTop: 8 }}
                            >
                                Creating lease audit...
                            </Typography>
                        </Box>
                    )}

                    {/* Error Message */}
                    {error && (
                        <Typography
                            color="error"
                            variant="body2"
                            style={{ marginTop: 16 }}
                        >
                            {error}
                        </Typography>
                    )}
                    {tokenBalanceError && (
                        <Typography
                            color="error"
                            variant="body2"
                            style={{ marginTop: 16 }}
                        >
                            {tokenBalanceError}
                        </Typography>
                    )}
                    {tokenBalance !== null && (
                        <Box
                            display="flex"
                            flexDirection="column"
                            style={{ marginTop: 16 }}
                        >
                            <Typography
                                variant="subtitle2"
                                className={classes.sectionTitle}
                            >
                                <AccountBalanceIcon
                                    className={classes.sectionIcon}
                                    style={{ color: '#10b981' }}
                                />
                                Tokens
                            </Typography>
                            <Box display="flex">
                                <Typography
                                    variant="body1"
                                    style={{ minWidth: 72 }}
                                >
                                    Cost:
                                </Typography>
                                <Typography variant="body1" align="right">
                                    {formatTokens(requiredTokens)}
                                </Typography>
                            </Box>
                            <Box display="flex">
                                <Typography
                                    variant="body1"
                                    style={{ minWidth: 72 }}
                                >
                                    Balance:
                                </Typography>
                                <Typography variant="body1" align="right">
                                    {formatTokens(tokenBalance)}
                                </Typography>
                            </Box>
                            {tokenBalance < requiredTokens && (
                                <Typography variant="body2" color="error">
                                    Balance insufficient
                                </Typography>
                            )}
                        </Box>
                    )}

                    {/* Actions */}
                    <Box className={classes.actions}>
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="flex-end"
                        >
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSubmit}
                                disabled={
                                    isCreating ||
                                    isLoadingTokenBalance ||
                                    !name.trim() ||
                                    !auditType ||
                                    !hasSufficientTokens
                                }
                                className={classes.submitButton}
                            >
                                {isCreating ? 'Creating...' : 'Create Audit'}
                            </Button>
                        </Box>
                    </Box>
                </Paper>
            </Slide>
        </Modal>
    )
}
